import {
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, Button, CircularProgress, Stack, useTheme } from '@mui/material';
import { Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  GetLeadsQueryVariables,
  LeadsKanbanColumnItemFragment,
  LeadsKanbanColumnLeadFragment,
} from '@graphql/__generated__/graphql';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import IndicatorLeadStage, {
  type IndicatorStatus,
} from '@modules/_shared/components/IndicatorLeadStage';
import { KanbanColumn } from '@modules/_shared/components/kanban/KanbanColumn';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import LeadsKanbanCard from '@modules/leads/LeadsKanbanCard';
import {
  type LeadKanbanItem,
  type MenuAction,
  PAGE_SIZE,
  buildLeadsKanbanQueryVars,
} from '@modules/leads/LeadsKanbanView';
import {
  GET_KANBAN_LEADS_AGENTS,
  GET_KANBAN_LEADS_COUNT,
} from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

export const LEADS_KANBAN_COLUMN_LEAD_FRAGMENT = gql(/* GraphQL */ `
  fragment LeadsKanbanColumnLead on leads {
    id
    created_by
    anonymized_contact
    origin_enquiry_id
    relationship
    appraisal_perceived_value
    appraisal_reason
    ...LeadAppraisalLocation
    sale_horizon
    buy_horizon
    ...LeadUpdatedTimeAgo
    contact {
      id
      first_name
      last_name
      emails(
        order_by: [{ primary: desc }, { verified: desc }, { created_at: desc }]
        limit: 1
      ) {
        id
        email
      }
    }
    lots {
      id
    }
    next_activity: activities(
      limit: 1
      where: {
        done: { _eq: false }
        activity_type: { _in: [call, visit, task, assignment] }
      }
      order_by: { due_at: asc }
    ) {
      id
      activity_type
      due_at
      start_date
    }
    property {
      id
      street_number
      route
      postcode
      locality
      state
      country
      latest_appraisal {
        id
        min
        max
      }
    }
  }
`);

export const LEADS_KANBAN_COLUMN_ITEM_FRAGMENT = gql(/* GraphQL */ `
  fragment LeadsKanbanColumnItem on lead_agents {
    id
    lead {
      ...LeadsKanbanColumnLead
    }
    claimed_at
    user_id
    source
    win_probability
    predicted_win_date
    stage_id
    user {
      id
      ...UserInfo_user
    }
  }
`);

const leadToLeadKanbanItem = (
  lead: LeadsKanbanColumnItemFragment | LeadsKanbanColumnLeadFragment,
): LeadKanbanItem => {
  if ('lead' in lead) {
    return {
      ...lead.lead,
      id: lead.id,
      broker: lead.user,
      predicted_win_date: lead.predicted_win_date,
      win_probability: lead.win_probability,
      stage_id: lead.stage_id,
      lead_id: lead.lead.id,
      lead_agents: [
        {
          claimed_at: lead.claimed_at,
          user_id: lead.user_id,
          source: lead.source,
        },
      ],
    };
  }

  return {
    ...lead,
    lead_id: lead.id,
    broker: null,
    lead_agents: [],
  };
};

type LeadsKanbanColumnProps = {
  where: GetLeadsQueryVariables['where'];
  columnId: string;
  label: string | ReactNode;
  status: IndicatorStatus;
  onItemClick: (lead: LeadKanbanItem) => void;
  index: number;
  onLeadUpdate: (leadId: string, action: MenuAction) => void;
  onItemsChange: (items: LeadKanbanItem[]) => void;
  isFallbackColumn?: boolean;
};

const LeadsKanbanColumn = memo(
  ({
    where,
    columnId,
    label,
    status,
    onItemClick,
    index,
    onLeadUpdate,
    onItemsChange,
    isFallbackColumn = false,
  }: LeadsKanbanColumnProps) => {
    const muiTheme = useTheme();
    const palette = (muiTheme.vars ?? muiTheme).palette;
    const [offset, setOffset] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const { t } = useLocale();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const { countLimit } = getPaginationVariables(
      searchParams,
      undefined,
      true,
    );

    const endControls = useMemo(
      () => (
        <Box
          sx={{
            p: 1,
          }}
        >
          <Button
            onClick={() => {
              searchParams.set('stage_id', columnId);
              setSearchParams(searchParams);
              navigate({
                pathname: './new',
                search: searchParams.toString(),
              });
            }}
            size="small"
            startIcon={<Plus size={16} />}
            sx={{
              mx: 'auto',
              color: 'text.secondary',
            }}
            fullWidth
          >
            {t('New lead')}
          </Button>
        </Box>
      ),
      [columnId, navigate, searchParams, setSearchParams, t],
    );

    const queryVariables = useMemo(
      () => buildLeadsKanbanQueryVars(isFallbackColumn, where, columnId),
      [isFallbackColumn, where, columnId],
    );

    const { data, networkStatus, fetchMore } = useQuery(
      GET_KANBAN_LEADS_AGENTS,
      {
        variables: queryVariables,
        skip: !isVisible,
        notifyOnNetworkStatusChange: true,
      },
    );

    const { data: countData, loading: countLoading } = useQuery(
      GET_KANBAN_LEADS_COUNT,
      {
        variables: {
          include_leads: queryVariables.include_leads,
          where: queryVariables.where,
          where_leads: queryVariables.where_leads,
          limit: countLimit,
        },
        skip: !isVisible,
        notifyOnNetworkStatusChange: true,
      },
    );

    const leads = (isFallbackColumn ? data?.leads : data?.lead_agents) ?? [];
    const totalCount =
      (isFallbackColumn
        ? countData?.leads_aggregate?.aggregate?.count
        : countData?.lead_agents_aggregate?.aggregate?.count) ?? 0;
    const hasMore = leads.length < totalCount;

    const isFullLoading = [
      NetworkStatus.loading,
      NetworkStatus.refetch,
    ].includes(networkStatus);

    const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

    const handleScroll = useCallback(() => {
      if (!hasMore || isFullLoading || isLoadingMore) {
        return;
      }

      const nextOffset = offset + PAGE_SIZE;

      fetchMore({
        variables: { offset: nextOffset },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          setOffset(nextOffset);

          return {
            ...prev,
            leads: [...(prev.leads ?? []), ...(fetchMoreResult.leads ?? [])],
            lead_agents: [
              ...(prev.lead_agents ?? []),
              ...(fetchMoreResult.lead_agents ?? []),
            ],
          };
        },
      });
    }, [hasMore, isFullLoading, isLoadingMore, offset, fetchMore]);

    const handleVisibilityChange = useCallback((visible: boolean) => {
      setIsVisible(visible);
    }, []);

    const leadItems = useMemo(() => {
      return (
        (isFallbackColumn
          ? data?.leads?.map(lead => leadToLeadKanbanItem(lead))
          : data?.lead_agents?.map(lead => leadToLeadKanbanItem(lead))) ?? []
      );
    }, [data, isFallbackColumn]);

    useEffect(() => {
      if (leadItems.length > 0) {
        onItemsChange(leadItems);
      }
    }, [leadItems, onItemsChange]);

    const countLabel = useMemo(() => {
      if (countLoading) {
        return '...';
      }

      return `${totalCount}${
        countLimit != null && totalCount >= countLimit ? '+' : ''
      }`;
    }, [countLoading, totalCount, countLimit]);

    return (
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <KanbanColumn
          title={
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <IndicatorLeadStage status={status}>{label}</IndicatorLeadStage>
              <CountBadge count={countLabel} sx={{ ml: 0 }} />
            </Stack>
          }
          colId={columnId}
          items={leadItems}
          renderItem={(lead: LeadKanbanItem) => (
            <LeadsKanbanCard
              key={lead.id}
              lead={lead}
              onUpdate={
                isFallbackColumn
                  ? undefined
                  : action => onLeadUpdate(lead.id, action)
              }
            />
          )}
          isReorderable={false}
          getItemId={(lead: LeadKanbanItem) => lead.id}
          onItemClicked={onItemClick}
          onScroll={handleScroll}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          endControls={isFallbackColumn ? undefined : endControls}
          index={index}
          onVisibilityChange={handleVisibilityChange}
          onClickHeaderCheckbox={() => {
            const currentValue = searchParams.get('edit');
            if (currentValue === 'true') {
              searchParams.delete('edit');
            } else {
              searchParams.set('edit', 'true');
            }
            setSearchParams(searchParams);
          }}
          canDragItem={() => !isFallbackColumn}
          isDroppable={!isFallbackColumn}
          customOverStyle={{
            position: 'relative',
            ':before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: palette.primary.light,
              opacity: 0.4,
              zIndex: 1,
            },
          }}
        />
        {isFullLoading && (
          <Stack
            sx={{
              position: 'absolute',
              inset: 0,
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor:
                'color-mix(in srgb, var(--mui-palette-background-default) 70%, transparent)',
              zIndex: 1,
            }}
          >
            <CircularProgress disableShrink size={24} />
          </Stack>
        )}
      </Box>
    );
  },
);

export default LeadsKanbanColumn;
